import React, { useState } from 'react';
import { X, Plus, Minus } from 'lucide-react';
import { formatCurrency } from '../../../utils/formatters';

interface SynergiesAnalysisProps {
  onClose: () => void;
  dealValue: number;
}

export default function SynergiesAnalysis({ onClose, dealValue }: SynergiesAnalysisProps) {
  const [synergies, setSynergies] = useState([
    { type: 'revenue', value: 0, description: '' },
    { type: 'cost', value: 0, description: '' }
  ]);

  const handleSynergyChange = (index: number, field: string, value: string | number) => {
    const newSynergies = [...synergies];
    newSynergies[index] = { ...newSynergies[index], [field]: value };
    setSynergies(newSynergies);
  };

  const totalSynergies = synergies.reduce((sum, synergy) => sum + Number(synergy.value), 0);
  const adjustedValue = dealValue + totalSynergies;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Synergies Analysis</h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        <div className="p-6">
          <div className="space-y-6">
            {synergies.map((synergy, index) => (
              <div key={index} className="space-y-4">
                <h4 className="font-medium text-gray-900">
                  {synergy.type === 'revenue' ? 'Revenue Synergies' : 'Cost Synergies'}
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Value</label>
                    <input
                      type="number"
                      value={synergy.value}
                      onChange={(e) => handleSynergyChange(index, 'value', Number(e.target.value))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <input
                      type="text"
                      value={synergy.description}
                      onChange={(e) => handleSynergyChange(index, 'description', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            ))}

            <div className="pt-4 border-t border-gray-200">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium text-gray-700">Base Deal Value</span>
                  <span>{formatCurrency(dealValue)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="font-medium text-gray-700">Total Synergies</span>
                  <span className="text-green-600">{formatCurrency(totalSynergies)}</span>
                </div>
                <div className="flex justify-between text-sm font-bold">
                  <span className="text-gray-900">Adjusted Deal Value</span>
                  <span className="text-gray-900">{formatCurrency(adjustedValue)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}